import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  TvAlert,
  TvButton,
  TvCard,
  TvCardBody,
  TvCode,
  TvDiv,
  TvFormGroup,
  TvH4,
  TvInput,
  TvLabel,
  TvOption,
  TvP,
  TvSmall,
  TvStrong,
} from '@trustvid/design-system';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import AiServices from '../../../../services/ai';
import { buildScopedPath } from '../../../../utils/account-context';
import { useActiveAccountId } from '../../../../context/account-branding-context';
import { Link } from 'react-router-dom';
import { ROUTE_PATHS, toScopedRoute } from '../../../../routes/path-map';
import { DESIGNER_AGENT_COMPARISON_CALLOUT } from '../constants/designerAgentPromptGuide';

const ASPECT_OPTIONS = [
  { value: '9:16', label: '9:16' },
  { value: '16:9', label: '16:9' },
  { value: '1:1', label: '1:1' },
];

const PIPELINE_STEPS = [
  'Retrieve trustvid_platform + trustvid_design',
  'Plan scenes (checked LLM)',
  'Senior Designer DesignSpec',
  'Resolve Pexels media',
  'Build portable draft',
];

const normalizeDraftPath = (data) => {
  if (!data || typeof data !== 'object') {
    return null;
  }
  const nextUrl = data.next_url || data.nextUrl || data.redirect_url;
  if (typeof nextUrl === 'string' && nextUrl.trim()) {
    return nextUrl.trim().replace(/^\/accounts\/\d+(?=\/)/, '') || nextUrl.trim();
  }
  const draftId = data.draft_id || data.draftId || data?.draft?.id;
  if (draftId) {
    return `/edit-draft-campaigns/${draftId}`;
  }
  return null;
};

/**
 * Additive Designer Agent prompt composer — does NOT replace Create Design AI Campaign.
 * @param {'page'|'dialog'} variant — dialog embeds in Create Design modal.
 */
const DesignerAgentPromptComposer = ({
  onCreated = null,
  variant = 'page',
}) => {
  const navigate = useNavigate();
  const activeAccountId = useActiveAccountId();
  const [prompt, setPrompt] = useState('');
  const [aspectRatio, setAspectRatio] = useState('9:16');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [progressStep, setProgressStep] = useState(-1);
  const [draftPath, setDraftPath] = useState(null);
  const [error, setError] = useState('');
  const isDialog = variant === 'dialog';

  const resetComposer = () => {
    setDraftPath(null);
    setProgressStep(-1);
    setError('');
  };

  const handleCreate = async () => {
    const trimmed = prompt.trim();
    if (!trimmed) {
      toast.error('Enter a prompt');
      return;
    }
    setError('');
    setDraftPath(null);
    setIsSubmitting(true);
    setProgressStep(0);
    try {
      for (let i = 0; i < PIPELINE_STEPS.length - 1; i += 1) {
        setProgressStep(i);
        // Visual progress only — API is a single request.
        await new Promise((resolve) => setTimeout(resolve, 120));
      }
      setProgressStep(PIPELINE_STEPS.length - 1);
      const response = await AiServices.createDesignerDraft({
        prompt: trimmed,
        aspectRatio,
        accountId: activeAccountId,
      });
      const data = response?.data || response;
      const path = normalizeDraftPath(data);
      if (!path) {
        throw new Error(data?.message || 'Designer Agent did not return a draft.');
      }
      setDraftPath(path);
      toast.success('Designer Agent draft ready');
      if (typeof onCreated === 'function') {
        onCreated({ ...data, draftPath: path });
      }
    } catch (err) {
      const message =
        err?.response?.data?.message || err?.message || 'Failed to create designer draft.';
      setError(message);
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openDraft = () => {
    if (!draftPath) return;
    navigate(buildScopedPath(activeAccountId, draftPath));
  };

  const progressPct = progressStep < 0
    ? 0
    : Math.round(((progressStep + 1) / PIPELINE_STEPS.length) * 100);

  return (
    <TvDiv data-testid="designer-agent-prompt-composer">
      <TvAlert color="info" className="mb-3" data-testid="designer-agent-additive-callout">
        <TvStrong>Additive path.</TvStrong>{' '}
        {DESIGNER_AGENT_COMPARISON_CALLOUT}{' '}
        Endpoint: <TvCode>/ai/designer-create-draft</TvCode>.
      </TvAlert>

      {!draftPath ? (
        <TvCard className="shadow-sm border-0" data-testid="designer-agent-compose-card">
          <TvCardBody>
            <TvFormGroup className="mb-3">
              <TvLabel htmlFor="designer-agent-prompt">Prompt</TvLabel>
              <TvInput
                id="designer-agent-prompt"
                type="textarea"
                rows={5}
                value={prompt}
                disabled={isSubmitting}
                data-testid="designer-agent-prompt"
                placeholder="30s vertical spring sale promo, upbeat, bold titles, CTA shop now"
                onChange={(event) => setPrompt(event.target.value)}
              />
            </TvFormGroup>
            <TvFormGroup className="mb-3">
              <TvLabel htmlFor="designer-agent-aspect">Aspect ratio</TvLabel>
              <TvInput
                id="designer-agent-aspect"
                type="select"
                value={aspectRatio}
                disabled={isSubmitting}
                data-testid="designer-agent-aspect"
                className="w-auto"
                onChange={(event) => setAspectRatio(event.target.value)}
              >
                {ASPECT_OPTIONS.map((option) => (
                  <TvOption key={option.value} value={option.value}>
                    {option.label}
                  </TvOption>
                ))}
              </TvInput>
            </TvFormGroup>
            {error ? <TvAlert color="danger">{error}</TvAlert> : null}
            {isSubmitting ? (
              <TvDiv className="mb-3" data-testid="designer-agent-progress">
                <TvP className="mb-1">{PIPELINE_STEPS[Math.max(progressStep, 0)]}</TvP>
                <TvDiv className="progress mb-2" style={{ height: 8 }}>
                  <TvDiv
                    className="progress-bar"
                    role="progressbar"
                    style={{ width: `${progressPct}%` }}
                    aria-valuenow={progressPct}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  />
                </TvDiv>
                <ul className="small text-muted mb-0">
                  {PIPELINE_STEPS.slice(0, progressStep + 1).map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ul>
              </TvDiv>
            ) : null}
            <TvDiv className="d-flex flex-wrap gap-2">
              <TvButton
                color="primary"
                disabled={isSubmitting || !prompt.trim()}
                data-testid="designer-agent-create"
                onClick={handleCreate}
              >
                {isSubmitting ? 'Building…' : 'Create with Designer Agent'}
              </TvButton>
              {!isDialog ? (
                <TvButton
                  color="secondary"
                  outline
                  tag={Link}
                  to={toScopedRoute(activeAccountId, ROUTE_PATHS.dashboard)}
                  data-testid="designer-agent-use-create-design"
                >
                  Use Create Design instead
                </TvButton>
              ) : null}
            </TvDiv>
          </TvCardBody>
        </TvCard>
      ) : (
        <TvCard className="shadow-sm border-0" data-testid="designer-agent-done-card">
          <TvCardBody>
            <TvSmall className="text-success fw-semibold">Draft ready</TvSmall>
            <TvH4 className="mt-2">Open your draft in the editor</TvH4>
            <TvP className="text-muted">
              Draft from Designer Agent. Stock + overlays + optional personalized audio — no actor video.
            </TvP>
            <TvDiv className="d-flex flex-wrap gap-2">
              <TvButton color="primary" data-testid="designer-agent-open-draft" onClick={openDraft}>
                Open editor
              </TvButton>
              <TvButton color="secondary" outline data-testid="designer-agent-new-prompt" onClick={resetComposer}>
                New prompt
              </TvButton>
            </TvDiv>
          </TvCardBody>
        </TvCard>
      )}
    </TvDiv>
  );
};

DesignerAgentPromptComposer.propTypes = {
  onCreated: PropTypes.func,
  variant: PropTypes.oneOf(['page', 'dialog']),
};

export default DesignerAgentPromptComposer;
